import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './SubscriptionToProjectWizard.css';
import {
    Subscription,
    ProjectFormData,
    SelectedSubscription,
    RtdKonto,
    CreationProgress,
    CreatedProject
} from './SubscriptionToProjectWizardTypes';
import SubscriptionToProjectWizardStep1 from './SubscriptionToProjectWizardStep1';
import SubscriptionToProjectWizardStep2 from './SubscriptionToProjectWizardStep2';
import SubscriptionToProjectWizardStep3 from './SubscriptionToProjectWizardStep3';

interface WizardProps {
    onClose: () => void;
    onSuccess: (projectId: number) => void;
    token: string;
}

const SubscriptionToProjectWizard: React.FC<WizardProps> = ({
    onClose,
    onSuccess,
    token
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState<string | null>(null);
    
    // Step 1: Project parameters
    const [projectData, setProjectData] = useState<ProjectFormData>({
        name: '',
        description: '',
        status: 'OPEN',
        rtdKontoId: null
    });
    const [rtdKontos, setRtdKontos] = useState<RtdKonto[]>([]);
    const [loadingRtdKontos, setLoadingRtdKontos] = useState(true);

    // Step 2: Subscription selection
    const [subscriptions, setSubscriptions] = useState<SelectedSubscription[]>([]);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Step 3 & Creation
    const [isCreating, setIsCreating] = useState(false);
    const [creationProgress, setCreationProgress] = useState<CreationProgress>({
        total: 0,
        current: 0,
        currentStep: ''
    });
    const [createdProject, setCreatedProject] = useState<CreatedProject | null>(null);
    const [showCompletion, setShowCompletion] = useState(false);

    // Fetch RTD Kontos
    useEffect(() => {
        const fetchRtdKontos = async () => {
            try {
                const response = await fetch('/api/bankaccounts/all', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch RTD Kontos');
                }

                const data: RtdKonto[] = await response.json();
                setRtdKontos(data);
            } catch (error) {
                console.error('Error fetching RTD Kontos:', error);
                toast.error('Fehler beim Laden der RTD Konten');
            } finally {
                setLoadingRtdKontos(false);
            }
        };

        fetchRtdKontos();
    }, [token]);

    // Fetch subscriptions
    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const response = await fetch('/api/subscriptions', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch subscriptions');
                }

                const data: Subscription[] = await response.json();
                setSubscriptions(data.map(sub => ({ subscription: sub, selected: false })));
            } catch (error) {
                console.error('Error fetching subscriptions:', error);
                toast.error('Fehler beim Laden der Abonnements');
            } finally {
                setLoadingSubscriptions(false);
            }
        };

        fetchSubscriptions();
    }, [token]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleNext = () => {
        setError(null);

        if (currentStep === 1) {
            if (!projectData.name.trim()) {
                setError('Bitte geben Sie einen Projektnamen ein');
                return;
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            const selectedCount = subscriptions.filter(s => s.selected).length;
            if (selectedCount === 0) {
                setError('Bitte wählen Sie mindestens ein Abonnement aus');
                return;
            }
            setCurrentStep(3);
        }
    };

    const handleBack = () => {
        setError(null);
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleConfirm = async () => {
        const selectedSubs = subscriptions.filter(s => s.selected);
        if (selectedSubs.length === 0) {
            setError('Bitte wählen Sie mindestens ein Abonnement aus');
            return;
        }

        setIsCreating(true);
        setError(null);

        // Group subscriptions by Round Table
        const subscriptionsByRoundTable = selectedSubs.reduce((acc, s) => {
            const rtId = s.subscription.ownerRoundTableId || 0;
            if (!acc[rtId]) {
                acc[rtId] = [];
            }
            acc[rtId].push(s.subscription);
            return acc;
        }, {} as Record<number, Subscription[]>);

        const totalInvoices = Object.keys(subscriptionsByRoundTable).length;
        
        setCreationProgress({
            total: totalInvoices + 1, // +1 for project creation
            current: 0,
            currentStep: 'Projekt wird erstellt...'
        });

        try {
            // Step 1: Create the project
            setCreationProgress(prev => ({ ...prev, currentStep: 'Projekt wird erstellt...' }));
            
            const projectResponse = await fetch('/api/projects', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: projectData.name,
                    description: projectData.description || null,
                    status: projectData.status,
                    rtdKontoId: projectData.rtdKontoId
                })
            });

            if (!projectResponse.ok) {
                throw new Error('Fehler beim Erstellen des Projekts');
            }

            const project = await projectResponse.json();
            setCreatedProject({ id: project.id, name: project.name });
            
            setCreationProgress(prev => ({ ...prev, current: 1 }));

            // Step 2: Create invoices for each Round Table
            let successCount = 0;
            let failCount = 0;
            let invoiceIndex = 0;

            for (const [rtId, subs] of Object.entries(subscriptionsByRoundTable)) {
                invoiceIndex++;
                const rtInfo = subs[0].ownerRoundTable;
                const rtName = rtInfo ? `RT ${rtInfo.number} - ${rtInfo.name}` : 'Kein Round Table';
                
                setCreationProgress(prev => ({
                    ...prev,
                    current: 1 + invoiceIndex,
                    currentStep: `Erstelle Rechnung für ${rtName}...`
                }));

                try {
                    // Create invoice positions from subscriptions
                    const positions = subs.map(sub => ({
                        description: sub.description || 'Abonnement',
                        pricePerItem: sub.annualPrice, // Already in cents
                        numberOfItems: 1,
                        tax: sub.taxRate
                    }));

                    const rechnungResponse = await fetch('/api/rechnungen', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            date: new Date().toISOString(),
                            projectId: project.id,
                            roundTableId: Number(rtId) !== 0 ? Number(rtId) : null,
                            positions: positions
                        })
                    });

                    if (!rechnungResponse.ok) {
                        throw new Error(`Fehler beim Erstellen der Rechnung für ${rtName}`);
                    }

                    successCount++;
                } catch (error) {
                    console.error(`Error creating invoice for ${rtName}:`, error);
                    failCount++;
                }
            }

            setIsCreating(false);
            setShowCompletion(true);

            // Show summary toast
            if (failCount === 0) {
                toast.success(`Projekt und ${successCount} Rechnung(en) erfolgreich erstellt`);
            } else if (successCount === 0) {
                toast.error(`Projekt erstellt, aber alle ${failCount} Rechnungen konnten nicht erstellt werden`);
            } else {
                toast.warning(`Projekt und ${successCount} Rechnung(en) erstellt, ${failCount} fehlgeschlagen`);
            }

        } catch (error) {
            console.error('Error creating project:', error);
            setError(error instanceof Error ? error.message : 'Fehler beim Erstellen des Projekts');
            toast.error(error instanceof Error ? error.message : 'Fehler beim Erstellen des Projekts');
            setIsCreating(false);
        }
    };

    const handleViewProject = () => {
        if (createdProject) {
            onSuccess(createdProject.id);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const selectedCount = subscriptions.filter(s => s.selected).length;

    return (
        <div className="subToProjWiz-wizard-overlay" onClick={handleOverlayClick}>
            <div className="subToProjWiz-wizard-modal subToProjWiz-wizard-modal-large">
                <div className="subToProjWiz-wizard-header">
                    <h2>Projekt aus Abonnements erstellen</h2>
                    <button
                        className="subToProjWiz-wizard-close-btn"
                        onClick={onClose}
                        aria-label="Schließen"
                        disabled={isCreating}
                    >
                        ✕
                    </button>
                </div>

                <div className="subToProjWiz-wizard-content">
                    {!showCompletion ? (
                        <>
                            {/* Step Indicator */}
                            <div className="subToProjWiz-wizard-step-indicator">
                                <div className={`subToProjWiz-step-item ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                                    <div className="subToProjWiz-step-number">1</div>
                                    <div className="subToProjWiz-step-label">Projekt-Parameter</div>
                                </div>
                                <div className="subToProjWiz-step-line"></div>
                                <div className={`subToProjWiz-step-item ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                                    <div className="subToProjWiz-step-number">2</div>
                                    <div className="subToProjWiz-step-label">Abonnements</div>
                                </div>
                                <div className="subToProjWiz-step-line"></div>
                                <div className={`subToProjWiz-step-item ${currentStep >= 3 ? 'active' : ''}`}>
                                    <div className="subToProjWiz-step-number">3</div>
                                    <div className="subToProjWiz-step-label">Bestätigung</div>
                                </div>
                            </div>

                            {/* Step Content */}
                            <div className="subToProjWiz-wizard-steps">
                                {currentStep === 1 && (
                                    <SubscriptionToProjectWizardStep1
                                        projectData={projectData}
                                        setProjectData={setProjectData}
                                        rtdKontos={rtdKontos}
                                        loadingRtdKontos={loadingRtdKontos}
                                    />
                                )}
                                {currentStep === 2 && (
                                    <>
                                        {loadingSubscriptions ? (
                                            <div className="subToProjWiz-loading-text">Lade Abonnements...</div>
                                        ) : (
                                            <SubscriptionToProjectWizardStep2
                                                subscriptions={subscriptions}
                                                setSubscriptions={setSubscriptions}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                            />
                                        )}
                                    </>
                                )}
                                {currentStep === 3 && (
                                    <SubscriptionToProjectWizardStep3
                                        projectData={projectData}
                                        selectedSubscriptions={subscriptions.filter(s => s.selected)}
                                        rtdKontos={rtdKontos}
                                    />
                                )}
                            </div>

                            {error && (
                                <div className="subToProjWiz-wizard-error">
                                    {error}
                                </div>
                            )}

                            {isCreating && (
                                <div className="subToProjWiz-wizard-progress">
                                    <p>{creationProgress.currentStep}</p>
                                    <div className="subToProjWiz-progress-bar">
                                        <div
                                            className="subToProjWiz-progress-bar-fill"
                                            style={{ width: `${(creationProgress.current / creationProgress.total) * 100}%` }}
                                        ></div>
                                    </div>
                                    <p className="subToProjWiz-progress-text">
                                        {creationProgress.current} von {creationProgress.total} Schritten abgeschlossen
                                    </p>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="subToProjWiz-wizard-actions">
                                <button
                                    type="button"
                                    className="subToProjWiz-wizard-btn subToProjWiz-wizard-btn-cancel"
                                    onClick={onClose}
                                    disabled={isCreating}
                                >
                                    Abbrechen
                                </button>
                                {currentStep > 1 && (
                                    <button
                                        type="button"
                                        className="subToProjWiz-wizard-btn subToProjWiz-wizard-btn-back"
                                        onClick={handleBack}
                                        disabled={isCreating}
                                    >
                                        Zurück
                                    </button>
                                )}
                                {currentStep < 3 ? (
                                    <button
                                        type="button"
                                        className="subToProjWiz-wizard-btn subToProjWiz-wizard-btn-submit"
                                        onClick={handleNext}
                                        disabled={isCreating}
                                    >
                                        Weiter
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="subToProjWiz-wizard-btn subToProjWiz-wizard-btn-submit"
                                        onClick={handleConfirm}
                                        disabled={isCreating}
                                    >
                                        {isCreating ? 'Erstelle...' : 'Bestätigen'}
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        /* Completion Screen */
                        <div className="subToProjWiz-wizard-completion">
                            <div className="subToProjWiz-completion-icon">✓</div>
                            <h3>Projekt erfolgreich erstellt!</h3>
                            <p className="subToProjWiz-completion-message">
                                Das Projekt "{createdProject?.name}" wurde erstellt und die Rechnungen wurden angelegt.
                            </p>
                            <div className="subToProjWiz-completion-actions">
                                <button
                                    type="button"
                                    className="subToProjWiz-wizard-btn subToProjWiz-wizard-btn-cancel"
                                    onClick={handleClose}
                                >
                                    Schließen
                                </button>
                                <button
                                    type="button"
                                    className="subToProjWiz-wizard-btn subToProjWiz-wizard-btn-submit"
                                    onClick={handleViewProject}
                                >
                                    Projekt anzeigen
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionToProjectWizard;
